/**
 * Cblurbackdrop Component
 * Reusable blur backdrop layer for modals and overlays.
 *
 * The blur radius and background are STATIC from mount; only opacity animates.
 * Tweening `backdrop-filter` made the browser re-sample and re-blur the whole
 * screen behind the layer on every frame, which could not settle while anything
 * behind it was still painting.
 */
'use client';
import type { CSSProperties, ReactElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { zIndex } from '@/theme/zIndex';
import { pxToRem } from '@/theme/spacing';
import { getBackgroundGradient } from '@/theme/gradients';
import { useViewport } from '@/hooks/dom/use-viewport';
import {
    BackdropFadeVariant,
    getBackdropExitEasing,
    getBackdropGradientStyle,
    getBackdropLayerKey,
    getBackdropParentStyle,
} from './backdrop-motion';
export { BackdropFadeVariant } from './backdrop-motion';
const NO_BACKGROUND_GRADIENT = 'none';
export interface CblurbackdropProps {
    visible: boolean;
    topOffset?: number; // in pixels, will be converted to rem
    opacity?: number;
    blur?: number; // blur amount in pixels, 0 for no blur
    useGradient?: boolean; // If true, uses page gradient instead of solid black
    zIndexOverride?: number; // Optional z-index override
    onClick?: () => void;
    className?: string;
    style?: CSSProperties;
    animationDuration?: number;
    /** Shape of the opacity envelope. Defaults to the legacy-matching curve. */
    fadeVariant?: BackdropFadeVariant;
}
function getBackdropTop(topOffset: number): string | number {
    if (topOffset <= 0) {
        return 0;
    }
    return pxToRem(topOffset);
}
function getBackdropStyle(input: {
    topOffset: number;
    zIndexOverride: number | undefined;
    useGradient: boolean;
    gradient: string;
    style: CSSProperties | undefined;
}): CSSProperties {
    return {
        position: 'fixed',
        top: getBackdropTop(input.topOffset),
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: input.zIndexOverride ?? zIndex.modalBackdrop,
        pointerEvents: 'auto',
        ...(input.useGradient && { backgroundImage: input.gradient }),
        ...input.style,
    };
}
function getBackdropTransition(animationDuration: number): {
    duration: number;
    ease: [
        number,
        number,
        number,
        number
    ];
} {
    return {
        duration: animationDuration / 1000,
        ease: [0.4, 0, 0.2, 1],
    };
}
export function Cblurbackdrop({ visible, topOffset = 0, opacity = 0.5, blur = 10, useGradient = false, zIndexOverride, onClick, className, style, animationDuration = 200, fadeVariant = BackdropFadeVariant.Matched, }: CblurbackdropProps): ReactElement {
    const { viewport } = useViewport();
    const gradient = viewport ? getBackgroundGradient(viewport) : NO_BACKGROUND_GRADIENT;
    const backdropStyle = getBackdropStyle({
        topOffset,
        zIndexOverride,
        useGradient,
        gradient,
        style,
    });
    const transition = getBackdropTransition(animationDuration);
    const exitEasing = getBackdropExitEasing(fadeVariant);
    const exitTransition = exitEasing ? { ...transition, ease: exitEasing } : transition;
    // Layer identity: everything baked statically into this layer. A config change
    // (a nested modal with different settings, or closing one to reveal another)
    // mounts a NEW layer and lets AnimatePresence crossfade it against the old one,
    // which keeps rendering at its own static blur while it exits. Mutating the live
    // layer instead would snap the full-screen blur in a single frame.
    const layerKey = getBackdropLayerKey({ blur, opacity, topOffset, useGradient, zIndexOverride });
    return (
    <AnimatePresence propagate>
      {visible && (<motion.div key={layerKey} className={className} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: exitTransition }} transition={transition} style={getBackdropParentStyle({ backdropStyle, blur, opacity, useGradient })} onClick={onClick}>
          {useGradient && (<div style={getBackdropGradientStyle({ gradient, opacity })}/>)}
        </motion.div>)}
    </AnimatePresence>
    );
}
